"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { vuotoDi, type MenuBarra } from "@/components/search/BarraRicerca";
import {
  ETICHETTE,
  ETICHETTA_FACCETTA,
  FiltriAttivi,
  GruppoFaccette,
  IconaX,
  Scelta,
  type FiltroAttivo,
} from "@/components/search/Faccette";
import {
  FUOCO,
  PULSANTE_CONTORNO,
  PULSANTE_PIENO,
} from "@/components/ui/pulsanti";

type Props = {
  /** I restringimenti, nella stessa forma dichiarativa che la barra
   *  rende sul sito: il pannello non è una seconda interfaccia con una
   *  seconda logica, è la stessa lista disegnata in verticale. */
  menu: MenuBarra[];
  /** Il testo di partenza (`?q=`). */
  testo: string;
  segnaposto: string;
  /** Applica testo e restringimenti **in una volta sola**. Il pannello
   *  tiene le scelte in locale e le consegna tutte insieme ad
   *  «Applica»: propagandole una per una, ogni tocco farebbe navigare
   *  la pagina sotto e il pannello si smonterebbe sotto le dita. */
  onApplica: (testo: string, valori: Record<string, string | string[]>) => void;
  onChiudi: () => void;
};

type Valori = Record<string, string | string[]>;

/** Dove sta un menu fra le tre domande di <RicercaRullo>: dove, di che
 *  cosa, quando. Le pagine dichiarano solo il parametro, e il parametro
 *  basta: `place` e `area` sono luoghi, `date` è il tempo, tutto il resto
 *  (categoria, tipo, i tipi dei corridoi) è tema. */
type Domanda = "Dove" | "Di che cosa" | "Quando";
const DOMANDE: Domanda[] = ["Dove", "Di che cosa", "Quando"];
function domandaDi(param: string): Domanda {
  if (param === "place" || param === "area") return "Dove";
  if (param === "date") return "Quando";
  return "Di che cosa";
}

/** Il nome del filtro. Per i parametri che hanno un nome fisso su tutto
 *  il sito (`ETICHETTE`) vale quello, così «Paese o area» si chiama allo
 *  stesso modo qui e sul Rullo; gli altri tengono il nome della pagina
 *  (la «Regione» di /report non è un paese). */
function nomeDi(m: MenuBarra): string {
  if (m.param === "place") return ETICHETTE.luogo;
  if (m.param === "cat") return ETICHETTE.categoria;
  if (m.param === "date") return ETICHETTE.periodo;
  if (m.param === "priority") return ETICHETTE.tipo;
  return m.etichetta;
}

/** Che cosa toglie un'etichetta dei filtri attivi: un menu intero, o
 *  una sola voce di un menu multiplo. */
type Togli = { param: string; voce?: string };

/**
 * I restringimenti a schermo intero, per il telefono.
 *
 * È **l'unica variante mobile della barra**: sotto `sm` la riga del
 * sito non ci sta — a 390 pixel misura più del viewport e i controlli
 * si schiacciano fino a diventare inservibili — quindi al suo posto
 * resta un bersaglio solo, che apre questa pagina. Le sezioni si
 * generano dalle stesse `MenuBarra` che la barra rende sul sito, quindi
 * qualunque pagina che abbia dei restringimenti ha il pannello, senza
 * una seconda logica da tenere allineata.
 *
 * I pezzi sono quelli di components/search/Faccette.tsx, gli stessi di
 * <RicercaRullo>: il nome visibile sopra ogni controllo, il `<select>`
 * nativo per le scelte singole (sul telefono apre il selettore del
 * sistema, che per un centinaio di paesi è meglio di qualunque elenco
 * disegnato), le caselle per le scelte multiple, i tre gruppi dove / di
 * che cosa / quando e la riga dei filtri attivi con le loro ✕. Prima il
 * pannello aveva una pelle sua (la cornice accento di `SEARCH_SHELL`,
 * pastiglie e tendine in monospazio), cioè una terza forma degli stessi
 * filtri accanto alla barra e al Rullo.
 *
 * «Applica» sta in un piede fermo fuori dalla parte che scorre: prima era
 * in fondo al modulo, e con il menu dei paesi aperto bisognava scorrere
 * fino alla fine per trovarlo.
 */
export function PannelloRicerca({
  menu,
  testo,
  segnaposto,
  onApplica,
  onChiudi,
}: Props) {
  const idTitolo = useId();
  const idModulo = useId();
  const idCampo = useId();
  const campo = useRef<HTMLInputElement>(null);

  // Le scelte restano qui finché non si preme "Applica".
  const [valori, setValori] = useState<Valori>(() =>
    Object.fromEntries(menu.map((m) => [m.param, m.valore])),
  );
  const [q, setQ] = useState(testo);

  // Blocca lo scorrimento della pagina sotto e chiudi con Escape
  // (tastiere esterne su tablet, oltre alla finestra stretta sul sito).
  useEffect(() => {
    const prec = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onChiudi();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prec;
      window.removeEventListener("keydown", onKey);
    };
  }, [onChiudi]);

  const imposta = (param: string, v: string | string[]) =>
    setValori((prec) => ({ ...prec, [param]: v }));

  const scelte = (m: MenuBarra): string[] => {
    const v = valori[m.param];
    return Array.isArray(v) ? v : v ? [v] : [];
  };
  /** Il nome di una voce nelle etichette dei filtri attivi, senza il
   *  conteggio che alcune pagine scrivono dentro la voce («Asia · 32»):
   *  il numero serve a scegliere, non a dire che cosa è acceso. */
  const etichettaDi = (m: MenuBarra, v: string) =>
    (m.opzioni.find((o) => o.value === v)?.label ?? v).replace(
      / · [\d.]+$/,
      "",
    );

  const filtri: FiltroAttivo<Togli>[] = menu.flatMap((m) =>
    m.multiplo
      ? scelte(m).map((v) => {
          const tono = m.opzioni.find((o) => o.value === v)?.tone;
          return {
            chiave: `${m.param}-${v}`,
            nome: `${nomeDi(m).toLowerCase()} ${etichettaDi(m, v)}`,
            etichetta: (
              <>
                {tono ? (
                  <span
                    aria-hidden
                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: tono }}
                  />
                ) : null}
                {etichettaDi(m, v)}
              </>
            ),
            togli: { param: m.param, voce: v },
          };
        })
      : scelte(m).map((v) => ({
          chiave: m.param,
          nome: `${nomeDi(m).toLowerCase()} ${etichettaDi(m, v)}`,
          etichetta: (
            <>
              {nomeDi(m)}: {etichettaDi(m, v)}
            </>
          ),
          togli: { param: m.param },
        })),
  );

  const togli = ({ param, voce }: Togli) => {
    const m = menu.find((x) => x.param === param);
    if (!m) return;
    imposta(param, m.multiplo ? scelte(m).filter((x) => x !== voce) : "");
  };

  const gruppi = DOMANDE.map((d) => ({
    legenda: d,
    menu: menu.filter((m) => domandaDi(m.param) === d),
  })).filter((g) => g.menu.length > 0);

  // Nessuna guardia sul montaggio: il pannello si rende solo dopo un
  // tocco, quindi non passa mai dal render lato server e
  // `document.body` esiste sempre.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitolo}
      className="fixed inset-0 z-[900] flex flex-col bg-bg"
    >
      {/* Testata: resta ferma mentre il corpo del pannello scorre. */}
      <div className="flex shrink-0 items-center gap-3 border-b border-rule bg-surface px-4 py-3">
        <h2
          id={idTitolo}
          className="m-0 font-mono text-nota font-semibold uppercase tracking-etichetta text-muted"
        >
          Cerca e filtra
        </h2>
        <button
          type="button"
          onClick={onChiudi}
          className={`ml-auto inline-flex min-h-[44px] cursor-pointer items-center gap-2 px-3.5 ${PULSANTE_CONTORNO}`}
        >
          <IconaX className="h-3.5 w-3.5" />
          Chiudi
        </button>
      </div>

      <form
        id={idModulo}
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          onApplica(q.trim(), valori);
        }}
        className="oi-scroll flex-1 overflow-y-auto"
      >
        <div className="flex flex-col gap-5 px-4 py-5">
          {/* Il campo, lo stesso di <RicercaRullo> senza il suo «Cerca»:
              qui l'invio è «Applica», nel piede, e vale per testo e
              filtri insieme. 17 pixel: sotto i 16 iOS ingrandisce la
              pagina appena il campo prende il fuoco. */}
          <div>
            <label htmlFor={idCampo} className={ETICHETTA_FACCETTA}>
              Cerca
            </label>
            <div className="flex min-h-[52px] min-w-0 items-stretch border border-sommesso/60 bg-bg focus-within:border-accent">
              <input
                ref={campo}
                id={idCampo}
                type="search"
                name="q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={segnaposto}
                autoComplete="off"
                enterKeyHint="search"
                className="min-w-0 flex-1 border-0 bg-transparent px-[15px] font-sans text-titolo text-ink placeholder:text-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent [&::-webkit-search-cancel-button]:hidden"
              />
              {q ? (
                <button
                  type="button"
                  onClick={() => {
                    setQ("");
                    campo.current?.focus();
                  }}
                  aria-label="Cancella il testo della ricerca"
                  className={`inline-flex w-11 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent text-muted hover:text-ink ${FUOCO}`}
                >
                  <IconaX className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>

          {/* I restringimenti, nei tre gruppi del Rullo, separati da un
              filetto. */}
          {gruppi.map((g) => (
            /* Il filetto sta su un involucro e non sul `<fieldset>`: sul
               fieldset il bordo passa a metà della legenda. */
            <div key={g.legenda} className="border-t border-rule pt-5">
              <GruppoFaccette
                legenda={g.legenda}
                className="flex flex-col gap-4"
              >
                {g.menu.map((m) =>
                  m.multiplo ? (
                    <Caselle
                      key={m.param}
                      menu={m}
                      nome={nomeDi(m)}
                      scelti={scelte(m)}
                      onCambia={(v) => imposta(m.param, v)}
                    />
                  ) : (
                    <Scelta
                      key={m.param}
                      etichetta={nomeDi(m)}
                      valore={scelte(m)[0] ?? ""}
                      vuoto={m.vuoto ?? vuotoDi(m.etichetta)}
                      gruppi={[
                        {
                          voci: m.opzioni.map((o) => ({
                            valore: o.value,
                            testo: o.label,
                          })),
                        },
                      ]}
                      onCambia={(v) => imposta(m.param, v)}
                    />
                  ),
                )}
              </GruppoFaccette>
            </div>
          ))}

          {/* Le scelte in attesa di «Applica», ognuna con la sua ✕. */}
          <FiltriAttivi
            filtri={filtri}
            onTogli={(f) => togli(f.togli)}
            testo={q.trim()}
            onCancellaTesto={() => setQ("")}
            onAzzera={() =>
              setValori(
                Object.fromEntries(
                  menu.map((m) => [m.param, m.multiplo ? [] : ""]),
                ),
              )
            }
            className="border-t border-rule pt-4"
          />
        </div>
      </form>

      {/* Il piede: «Applica» sempre a portata di pollice. */}
      <div className="shrink-0 border-t border-rule bg-surface px-4 py-3">
        <button
          type="submit"
          form={idModulo}
          className={`inline-flex min-h-[52px] w-full cursor-pointer items-center justify-center ${PULSANTE_PIENO}`}
        >
          Applica
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** Un filtro a scelta multipla, come il menu dei tipi di <RicercaRullo>
 *  ma disteso: sul pannello c'è posto e le caselle si vedono tutte senza
 *  aprire niente.
 *
 *  Nessuna scelta vuol dire «tutti», quindi in quel caso le caselle sono
 *  tutte spuntate e si restringe togliendo. L'ultima spuntata non si
 *  toglie (una scelta vuota di fatto sarebbe una lista vuota), e
 *  rispuntarle tutte torna a «nessuna scelta», così l'indirizzo non si
 *  porta dietro l'elenco intero. */
function Caselle({
  menu,
  nome,
  scelti,
  onCambia,
}: {
  menu: MenuBarra;
  nome: string;
  scelti: string[];
  onCambia: (v: string[]) => void;
}) {
  const tutte = menu.opzioni.map((o) => o.value);
  const attivi = scelti.length ? scelti : tutte;

  const cambia = (v: string) => {
    const nuovi = attivi.includes(v)
      ? attivi.filter((x) => x !== v)
      : tutte.filter((x) => x === v || attivi.includes(x));
    onCambia(nuovi.length === tutte.length ? [] : nuovi);
  };

  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className={`${ETICHETTA_FACCETTA} p-0`}>{nome}</legend>
      <ul
        className={`m-0 list-none border bg-bg p-1 ${
          scelti.length
            ? "border-accent shadow-[inset_3px_0_0_var(--color-accent)]"
            : "border-rule"
        }`}
      >
        {menu.opzioni.map((o) => {
          const dentro = attivi.includes(o.value);
          const ultimo = dentro && attivi.length === 1;
          return (
            <li key={o.value}>
              <label
                className={`flex min-h-[44px] items-center gap-3 px-3 font-sans text-voce text-ink has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent ${
                  ultimo ? "cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <input
                  type="checkbox"
                  checked={dentro}
                  disabled={ultimo}
                  onChange={() => cambia(o.value)}
                  className="h-4 w-4 shrink-0 cursor-pointer accent-[var(--color-ink)] disabled:cursor-not-allowed"
                />
                {o.tone ? (
                  <span
                    aria-hidden
                    className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: o.tone }}
                  />
                ) : null}
                <span className="min-w-0 truncate">{o.label}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {scelti.length ? (
        <button
          type="button"
          onClick={() => onCambia([])}
          className={`mt-2 inline-flex min-h-[40px] cursor-pointer items-center border-0 bg-transparent px-1 font-sans text-meta font-semibold text-accent underline underline-offset-4 hover:text-ink ${FUOCO}`}
        >
          {menu.vuoto ?? vuotoDi(menu.etichetta)}
        </button>
      ) : null}
    </fieldset>
  );
}
